import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { useId, useRef, useState } from 'react';
import { LuSlidersHorizontal } from 'react-icons/lu';
import { Controller, FormProvider, type UseFormReturn } from 'react-hook-form';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Dropdown } from 'primereact/dropdown';
import { chatCompanionRoles } from '../model/roleOptions';
import { MultiSelect } from 'primereact/multiselect';
import { ClientSearchField } from '@/entities/Client/ui/ClientSearchField';
import { ExecutorSearchField } from '@/entities/Executor/ui/ExecutorSearchField';
import type { ChatDialogSearchFilter } from '@/entities/ChatDialog/model/types';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { createRHFFields } from '@/shared/ui/RHFFields';
import type { ChatInboxFilterValues } from '../model/types';
import {
    chatInboxFilterToSearch,
    clientToChatFilterPerson,
    executorToChatFilterPerson,
    createChatInboxFilterValues,
} from '../mappers';
import classes from './ChatInboxFilters.module.scss';

const { TextField } = createRHFFields<ChatInboxFilterValues>();

const channels = [
    {
        id: 1,
        name: 'Приложение VOX'
    },
    {
        id: 2,
        name: 'Telegram'
    },
    {
        id: 3,
        name: 'VK'
    },
    {
        id: 4,
        name: 'Avito'
    },
    {
        id: 5,
        name: 'MAX'
    },
];

/** Показывает компактный поиск переписки */
export const ChatInboxFilters = (
    { onApply, form }: {
        form: UseFormReturn<ChatInboxFilterValues>;
        /** Применяет выбранные условия */
        onApply: (filter: ChatDialogSearchFilter) => void;
    }
) => {
    const panel = useRef<OverlayPanel>(null);
    const panelId = useId();
    const [open, setOpen] = useState(false);
    const canSearchClients = useCanAccessPermission('admin.clients.view');
    const canSearchExecutors = useCanAccessPermission('admin.executors.view');

    const clients = form.watch('clients');
    const executors = form.watch('executors');

    /** Применяет поиск и закрывает панель */
    const apply = (values: ChatInboxFilterValues): void => {
        onApply(chatInboxFilterToSearch(values));
        panel.current?.hide();
    };

    return <>
        <ActionButton
            type="button"
            icon={<LuSlidersHorizontal
                aria-hidden="true"
                focusable="false"
            />}
            iconOnly
            text
            size="small"
            color="secondary"
            className={classes.trigger}
            aria-label="Фильтры переписки"
            aria-expanded={open}
            aria-controls={open
                ? panelId
                : undefined
            }
            aria-haspopup="dialog"
            title="Фильтры переписки"
            onClick={(event) => panel.current?.toggle(event)}
        />
        <OverlayPanel
            ref={panel}
            id={panelId}
            onShow={() => setOpen(true)}
            onHide={() => setOpen(false)}
            showCloseIcon
            ariaCloseLabel="Закрыть фильтры"
            className={classes.panel}
        >
            <FormProvider {...form}>
                <form
                    className={classes.form}
                    aria-label="Фильтры переписки"
                    onSubmit={form.handleSubmit(apply)}
                >
                    <h3>Фильтры переписки</h3>
                    <TextField
                        name="companionName"
                        isRequired={false}
                        label="Имя собеседника"
                        placeholder="Имя или фамилия в VOX или Salebot"
                    />
                    <label htmlFor={`${panelId}-role`}>Собеседники VOX</label>
                    <Controller
                        name="companionRole"
                        control={form.control}
                        render={({ field }) => (
                            <Dropdown
                                inputId={`${panelId}-role`}
                                value={field.value ?? null}
                                options={chatCompanionRoles}
                                optionLabel="name"
                                optionValue="id"
                                onChange={(event) => field.onChange(event.value)}
                                onBlur={field.onBlur}
                            />
                        )}
                    />
                    <ClientSearchField
                        name="chatClient"
                        disabled={!canSearchClients}
                        label="Клиенты"
                        value={null}
                        onChange={(_id, client) => {
                            const person = client && clientToChatFilterPerson(client);

                            if (person && !clients.some((item) => item.id === person.id)) {
                                form.setValue('clients', [...clients, person]);
                            }
                        }}
                    />
                    <div className={classes.chips}>
                        {clients.map((person) => <button
                            key={person.id}
                            type="button"
                            aria-label={`Убрать клиента ${person.name}`}
                            onClick={() => form.setValue('clients',
                                clients.filter((item) => item.id !== person.id))}
                        >
                            {person.name} ×
                        </button>)}
                    </div>
                    <ExecutorSearchField
                        name="chatExecutor"
                        disabled={!canSearchExecutors}
                        label="Исполнители"
                        value={null}
                        onChange={(executor) => {
                            const person = executor && executorToChatFilterPerson(executor);

                            if (person && !executors.some((item) => item.id === person.id)) {
                                form.setValue('executors', [...executors, person]);
                            }
                        }}
                    />
                    <div className={classes.chips}>
                        {executors.map((person) => <button
                            key={person.id}
                            type="button"
                            aria-label={`Убрать исполнителя ${person.name}`}
                            onClick={() => form.setValue('executors',
                                executors.filter((item) => item.id !== person.id))}
                        >
                            {person.name} ×
                        </button>)}
                    </div>
                    <TextField
                        name="petName"
                        isRequired={false}
                        label="Имя питомца"
                        placeholder="Например, Бруно"
                    />
                    <label htmlFor="chatChannels">Каналы связи</label>
                    <Controller
                        name="channels"
                        control={form.control}
                        render={({ field }) => (
                            <MultiSelect
                                inputId="chatChannels"
                                value={field.value}
                                options={channels}
                                optionLabel="name"
                                optionValue="id"
                                display="chip"
                                placeholder="Все каналы"
                                onChange={(event) => field.onChange(event.value)}
                                onBlur={field.onBlur}
                            />
                        )}
                    />
                    <div className={classes.actions}>
                        <ActionButton
                            type="button"
                            label="Сбросить"
                            color="secondary"
                            onClick={() => {
                                const empty = createChatInboxFilterValues();
                                form.reset(empty);
                                apply(empty);
                            }}
                        />
                        <ActionButton
                            type="submit"
                            label="Применить"
                        />
                    </div>
                </form>
            </FormProvider>
        </OverlayPanel>
    </>;
};
